import { useFormik, FormikProvider } from "formik";
import * as Yup from "yup";
import { useEffect, useState } from "react";
import {
  emptyEmployee,
  getTypeEmployeeOptions,
  IEmployee,
} from "../../../types/employee.type";
import InputFormik from "../../../common/components/form/InputFormik";
import ButtonWithLoader from "../../../common/components/ui/loading/ButtonLoading";
import ComboBoxFormik from "../../../common/components/form/ComboBoxFormik";
import Permission from "@/common/components/auth/Permission";
import { useGetCompanies } from "@/common/hooks";
import { maskPhone } from "@/common/helpers/mask.helpers";
import ComboBoxMulti from "@/common/components/form/ComboBoxMulti";

type FormPropsCreatePartners = {
  onSubmit: (
    values: IEmployee
  ) => Promise<{ ok: boolean; data: IEmployee; errors?: unknown[] }>;
  data: IEmployee;
  formRequestErrors?: { message: string; path: string[] }[];
};

const validationSchema = Yup.object({
  name: Yup.string().required("Nome é obrigatório."),
  type: Yup.array()
    .of(Yup.string())
    .min(1, "Pelo menos uma permissão é obrigatória.")
    .required("Permissão é obrigatória."),
  email: Yup.string().email("Email inválido").required("Email é obrigatório"),
  position: Yup.array()
    .of(Yup.string())
    .min(1, "Pelo menos um cargo é obrigatório.")
    .required("Cargo é obrigatório."),
  active: Yup.boolean(),
  phone: Yup.string(),

  companyId: Yup.string().when("position", {
    is: (pos: string[] = []) => !pos.includes("Consultor Comercial"),
    then: (schema) => schema.required("Cidade é obrigatória."),
    otherwise: (schema) => schema.notRequired(),
  }),

  companyIds: Yup.array()
    .of(Yup.string())
    .when("position", {
      is: (pos: string[] = []) => pos.includes("Consultor Comercial"),
      then: (schema) => schema.min(1).required(),
      otherwise: (schema) => schema.notRequired(),
    }),
});

function FormEditEmployeeData(props: FormPropsCreatePartners) {
  const [isCreating, setIsCreating] = useState(false);
  const { data: companies } = useGetCompanies();

  const formik = useFormik<IEmployee>({
    initialValues: (() => {
      const base = isCreating ? emptyEmployee : (props.data ?? emptyEmployee);

      return {
        ...base,
        companyIds: base.companyIds ?? (base.companyId ? [base.companyId] : []),
      };
    })(),
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (data) => {
      const result = await props.onSubmit(data);
      if (result.ok && result.data) {
        formik.setValues({
          ...result.data,
          companyIds: result.data.companyIds ?? data.companyIds ?? [],
          companyId:
            result.data.companyId ??
            result.data.companyIds?.[0] ??
            data.companyId,
        });
      }
      return result;
    },
  });
  const companyOptions =
    companies?.map((company) => ({
      value: company._id ?? "",
      label: company.address.city,
    })) || [];
  useEffect(() => {
    setIsCreating(!props.data);
  }, [props.data]);

  const isConsultorComercial =
    formik.values.position?.includes("Consultor Comercial");

  useEffect(() => {
    const { companyId, companyIds = [] } = formik.values;

    if (isConsultorComercial) {
      if (!companyIds.length && companyId) {
        formik.setFieldValue("companyIds", [companyId]);
      }
      if (companyIds.length && companyId !== companyIds[0]) {
        formik.setFieldValue("companyId", companyIds[0]);
      }
      return;
    }

    const expected = companyId ? [companyId] : [];

    if (companyIds.join() !== expected.join()) {
      formik.setFieldValue("companyIds", expected);
    }
  }, [isConsultorComercial, formik, formik.values.companyId, formik.values.companyIds]);

  return (
    <form onSubmit={formik.handleSubmit} className="w-full">
      <FormikProvider value={formik}>
        <section className="mx-auto w-full max-w-5xl px-1 sm:px-0">
          <h2 className="mb-4 text-xl font-semibold sm:text-2xl">
            Informações Pessoais
          </h2>
          <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2 lg:gap-4 xl:grid-cols-3">
            <InputFormik
              className="min-w-0"
              label="Nome do Colaborador:"
              name="name"
              required
            />
            <InputFormik
              className="min-w-0"
              label="E-mail do Colaborador:"
              name="email"
              required
            />
            <InputFormik
              className="min-w-0"
              label="Telefone do Colaborador:"
              name="phone"
              required
              mask={maskPhone}
            />

            <ComboBoxMulti
              className="min-w-0"
              name="type"
              label="Permissão:"
              disabled={!!props.data._id}
              options={getTypeEmployeeOptions()}
            />

            <ComboBoxMulti
              className="min-w-0"
              required
              name="position"
              label="Cargo do Colaborador:"
              options={[
                { value: "", label: "Selecione o cargo..." },
                { value: "Gerente Comercial", label: "Gerente Comercial" },
                { value: "Consultor Comercial", label: "Consultor Comercial" },
                { value: "Mecânico", label: "Mecânico" },
                { value: "Chefe de oficina", label: "Chefe de oficina" },
                { value: "Gerente de cidade", label: "Gerente de cidade" },
                { value: "Gerente de região", label: "Gerente de região" },
                { value: "Preparador", label: "Preparador" },
                { value: "Pós-Atendimento", label: "Pós-Atendimento" },
                {
                  value: "Assistente de Relacionamento",
                  label: "Assistente de Relacionamento",
                },
                { value: "Marketing", label: "Marketing" },
                {
                  value: "Assistente financeiro",
                  label: "Assistente financeiro",
                },
                { value: "Administrativo", label: "Administrativo" },
                {
                  value: "Assistente Administrativo",
                  label: "Assistente Administrativo",
                },
                { value: "Estoquista", label: "Estoquista" },
                { value: "Lavador", label: "Lavador" },
                { value: "RH", label: "RH" },
              ]}
            />

            {isConsultorComercial ? (
              <ComboBoxMulti
                className="min-w-0"
                name="companyIds"
                label="Cidade:"
                required
                options={companyOptions}
              />
            ) : (
              <ComboBoxFormik
                className="min-w-0"
                name="companyId"
                label="Cidade:"
                required
                options={[
                  { value: "", label: "Selecione a cidade..." },
                  ...companyOptions,
                ]}
              />
            )}
          </div>

          <div className="mt-5 flex flex-col gap-3 rounded-lg bg-gray-50 p-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-5">
            <div className="flex items-start gap-2 sm:items-center">
              <input
                id="active"
                type="checkbox"
                {...formik.getFieldProps("active")}
                checked={formik.values.active}
                className="mt-1 h-4 w-4 sm:mt-0"
              />
              <label htmlFor="active" className="text-sm leading-5">
                Colaborador Ativo
              </label>
            </div>
            <Permission role="admin">
              <div className="flex items-start gap-2 sm:items-center">
                <input
                  id="viewAll"
                  type="checkbox"
                  {...formik.getFieldProps("viewAll")}
                  checked={formik.values.viewAll}
                  className="mt-1 h-4 w-4 sm:mt-0"
                />
                <label htmlFor="viewAll" className="text-sm leading-5">
                  Visualizar todas empresas
                </label>
              </div>
            </Permission>
          </div>
        </section>
        <div className="flex w-full justify-end px-1 pb-2 pt-5 sm:px-0">
          <ButtonWithLoader
            className="button-with-loader mt-0 w-full sm:w-auto sm:min-w-[180px]"
            isLoading={formik.isSubmitting}
            disabled={!formik.isValid}
            onClick={formik.handleSubmit}
          >
            Salvar
          </ButtonWithLoader>
        </div>
      </FormikProvider>
    </form>
  );
}

export default FormEditEmployeeData;
